import { memo, useEffect, useRef } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import type { PulseContentItem } from '../types';
import { usePulseStore } from '../hooks/usePulseStore';

/** Maximum items shown in the recent row. */
const MAX_RECENT = 20;

/**
 * Bottom ticker row showing the most recently uploaded videos in chronological order.
 * New videos appear on the right and older ones animate off the left edge.
 * Uses a horizontal flexbox with CSS transition on transform for the sliding effect.
 */
export function PulseRecentRow() {
  const recentItems = usePulseStore((s) => s.recentItems);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  // When new items appear, animate the container sliding left.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const currentCount = recentItems.length;
    const added = currentCount - prevCountRef.current;
    prevCountRef.current = currentCount;

    if (added > 0) {
      // Each item is ~120px wide. Shift right by added*120, then animate back to 0.
      const shift = added * 120;
      container.style.transition = 'none';
      container.style.transform = `translateX(${shift}px)`;
      requestAnimationFrame(() => {
        container.style.transition = 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)';
        container.style.transform = 'translateX(0)';
      });
    }
  }, [recentItems]);

  if (recentItems.length === 0) return null;

  return (
    <div className="pulse-recent-row relative h-[120px] shrink-0 overflow-hidden border-t border-white/5 bg-black/40">
      {/* Label */}
      <div className="absolute left-2 top-1 z-10 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-white/50">
        Recent uploads
      </div>

      {/* Scrolling container — items flow right-to-left */}
      <div
        ref={containerRef}
        className="flex h-full items-stretch gap-0"
        style={{ direction: 'rtl' }}
      >
        {recentItems.slice(0, MAX_RECENT).map((item) => (
          <RecentCell key={item.contentId} item={item} />
        ))}
      </div>
    </div>
  );
}

/** A single cell in the recent row — small thumbnail or muted video preview. */
const RecentCell = memo(function RecentCell({ item }: { item: PulseContentItem }) {
  const isVideo = !!item.playbackUrl;

  return (
    <div
      className="pulse-recent-cell relative h-full shrink-0 overflow-hidden"
      style={{ width: '120px', direction: 'ltr' }}
    >
      <div className="absolute inset-0 bg-black">
        {isVideo ? (
          <MuxPlayer
            src={item.playbackUrl}
            poster={item.thumbnailUrl || undefined}
            autoPlay="muted"
            muted
            loop
            playsInline
            preload="metadata"
            style={{ width: '100%', height: '100%' }}
            // @ts-expect-error - mux-player custom attributes
            nohotkeys=""
            primaryColor="transparent"
            secondaryColor="transparent"
          />
        ) : item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt="" className="size-full object-cover" />
        ) : (
          <div className="size-full bg-[#1a1a2e]" />
        )}
      </div>
      {/* NEW flash on entry */}
      <div className="pulse-recent-new-badge absolute right-1 top-1">
        <span className="rounded bg-[#0194f5]/80 px-1 py-0.5 text-[8px] font-bold text-white">
          NEW
        </span>
      </div>
      <div className="absolute bottom-0.5 left-0.5 max-w-[90%] truncate text-[8px] text-white/60">
        @{item.creatorUsername}
      </div>
    </div>
  );
});
